import React, {
    ReactElement, useState,
} from "react";
import { Form } from "react-bootstrap";
import BooleanSelect from "../../../../components/pickers/boolean/BooleanSelect";
import TagList from "../../../../components/tag-list/TagList";
import {
    CloseSettingsDocument, GetSettingsDocument, RequiredField, TagsSettingsDocument, useSaveSettingsMutation,
} from "../../../../__generated__/api";
import { SettingsProps } from "../base";
import useArticleContext from "../../../../contexts/HelpContext";
import useDirtyTracker from "../../../../components/dirty-tracker/DirtyTracker";
import UserGroups from "../../../../components/pickers/user-groups/UserGroups";
import Select from "../../../../components/select/Select";

const EVERYONE_GROUP = "urn:fq:user-group:everyone";

const TICKET_ASSIGNED = {
    label: "Assigned Agent",
    value: "assigned",
    data: [] as string[],
};
const TICKET_SPECIFIC = {
    label: "Assigned Agent and Specific Groups",
    value: "specific",
    data: [] as string[],
};
const TICKET_EVERYONE = {
    label: "All Agents",
    value: "everyone",
    data: [EVERYONE_GROUP],
};

const OVERRIDE_TICKET_PERMISSIONS = [
    TICKET_ASSIGNED,
    TICKET_EVERYONE,
    TICKET_SPECIFIC,
];

export default function Customization(props: SettingsProps): ReactElement {
    const [save] = useSaveSettingsMutation();
    useArticleContext("urn:fq:article:e0852e121db14aec98bf149ab1574a38");

    const [requiredCloseFields, setRequiredCloseFields] = useState(props.settings?.customization?.requiredCloseFields);
    const [tags, setTags] = useState(props.settings?.customization?.tags);
    const [allowOverrideTicketsGroups, setAllowOverrideTicketsGroups] = useState(
        props.settings?.customization?.allowOverrideTicketsGroups,
    );

    const dirtyTracker = useDirtyTracker({
        finishedSavingMessage: "Settings saved!",
        onSave: (event, onDone) => {
            event.preventDefault();
            (async () => {
                await save({
                    refetchQueries: [
                        { query: CloseSettingsDocument },
                        { query: TagsSettingsDocument },
                        { query: GetSettingsDocument },
                    ],
                    variables: {
                        customization: {
                            requiredCloseFields,
                            tags,
                            allowOverrideTicketsGroups,
                        },
                    },
                });
                onDone();
            })();
        },
    });

    dirtyTracker.useWatchProperty(requiredCloseFields, props.settings?.customization?.requiredCloseFields);
    dirtyTracker.useWatchProperty(tags, props.settings?.customization?.tags);
    dirtyTracker.useWatchProperty(allowOverrideTicketsGroups, props.settings?.customization?.allowOverrideTicketsGroups);

    dirtyTracker.useLoad(() => {
        setRequiredCloseFields(props.settings?.customization?.requiredCloseFields);
        setTags(props.settings?.customization?.tags);
        setAllowOverrideTicketsGroups(props.settings?.customization?.allowOverrideTicketsGroups?.isNotEmpty
            ? props.settings?.customization?.allowOverrideTicketsGroups
            : undefined);
    }, [props.settings]);

    const createRequiredFieldHandler = (field: RequiredField) => (value: boolean) => {
        let copy = Array.from(requiredCloseFields ?? []);
        if (value) {
            copy.push(field);
        } else {
            copy = copy.filter(item => item !== field);
        }
        setRequiredCloseFields(copy.dedupe());
    };

    let allowTicketGroup = TICKET_ASSIGNED;
    if (allowOverrideTicketsGroups?.some(item => item === EVERYONE_GROUP)) {
        allowTicketGroup = TICKET_EVERYONE;
    } else if (allowOverrideTicketsGroups != null) {
        allowTicketGroup = TICKET_SPECIFIC;
    }

    return (
        <form className="flex-grow-1">

            <div className="border p-3 rounded mb-4 bg-white">
                <h5 className="mb-3 text-light">
                    Required Fields
                </h5>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Require Tags to close</Form.Label>
                    <BooleanSelect
                        className="text-gray"
                        isRequired
                        value={requiredCloseFields?.includes(RequiredField.Tags) ?? false}
                        onChange={createRequiredFieldHandler(RequiredField.Tags)}
                    />
                    <Form.Text>
                        Should tags be required to end a conversation?
                    </Form.Text>
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Require Notes to close</Form.Label>
                    <BooleanSelect
                        className="text-gray"
                        isRequired
                        value={requiredCloseFields?.includes(RequiredField.TicketNotes) ?? false}
                        onChange={createRequiredFieldHandler(RequiredField.TicketNotes)}
                    />
                    <Form.Text>
                        Should comments be required to end a conversation?
                    </Form.Text>
                </Form.Group>

                <Form.Group>
                    <Form.Label className="fw-bolder d-block">Manage tickets</Form.Label>
                    <Form.Text className="d-block mb-2 mt-0">
                        Who is allowed to manage tickets such as take ownership, send messages, change properties,
                        and close tickets.
                    </Form.Text>
                    <Select
                        className="text-gray mb-2"
                        placeholder=""
                        onChange={value => {
                            if (value === TICKET_ASSIGNED) {
                                setAllowOverrideTicketsGroups(undefined);
                            } else if (value === TICKET_EVERYONE) {
                                setAllowOverrideTicketsGroups([EVERYONE_GROUP]);
                            } else {
                                setAllowOverrideTicketsGroups([]);
                            }
                        }}
                        options={OVERRIDE_TICKET_PERMISSIONS}
                        value={allowTicketGroup}
                    />

                    {allowTicketGroup === TICKET_SPECIFIC && (
                        <>
                            <p className="mt-2 mb-0">Which groups of users are allowed to edit settings?</p>
                            <UserGroups
                                isMulti
                                minRequired={1}
                                className="w-100"
                                onSelected={groupIds => {
                                    setAllowOverrideTicketsGroups(groupIds);
                                }}
                                value={allowOverrideTicketsGroups ?? []}
                            />
                        </>
                    )}

                </Form.Group>

            </div>
            <div className="border p-3 rounded mb-5 bg-white">
                <h5 className="mb-3 text-light">
                    Other Settings
                </h5>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Ticket Tags</Form.Label>
                    <TagList
                        value={tags as string[] ?? []}
                        onChange={value => setTags(value)}
                        isTag
                        canNotHaveDuplicates
                        shouldSort
                    />
                    <Form.Text>
                        These are the tags available when managing tickets.
                    </Form.Text>
                </Form.Group>

            </div>

            {dirtyTracker.saveComponent}

        </form>
    );
}
